import { useRef } from 'react';
import { Trash2, Plus } from 'lucide-react';
import { buildSlug } from '../utils/slug';
import ImageUploadField from './ImageUploadField';

const BUCKET = 'products_images';

const labelStyle = {
  display: 'block',
  fontSize: '0.82rem',
  fontWeight: '600',
  color: '#555',
  marginBottom: '0.3rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const inputStyle = {
  width: '100%',
  padding: '0.5rem 0.65rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.88rem',
  outline: 'none',
  boxSizing: 'border-box',
};

// Derives a stable id from the variant name, deduped against ids already in
// use by this product's other variants. Once a variant has an id it keeps it
// for life — cart items and past orders reference variants by id, so
// renaming a color must never change which variant historic orders point to.
function slugForName(name, existingIds) {
  const base = buildSlug(name);
  if (!existingIds.has(base)) return base;
  let n = 2;
  while (existingIds.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

/**
 * Repeatable color-variant editor: name (ES/EN), swatch color, quantity, and
 * front/back images shown on the product page when the shopper picks that
 * color. The back image is optional — the product's own imageBack is used
 * as a fallback when a variant doesn't have its own. Used by
 * AdminProductForm.
 *
 * Props:
 *   value {Array}       — [{id,name,name_en,hex,image,imageBack,quantity}]
 *   onChange(next)      — called with the updated array
 *   onUploadingChange(bool) — bubbled up so the parent form can disable submit
 */
const ColorVariantsField = ({ value = [], onChange, onUploadingChange }) => {
  // A ref, not state — this only drives the onUploadingChange side effect,
  // it never needs to trigger a re-render of this component itself.
  const uploadingIds = useRef(new Set());

  // Keyed by `${idx}-front` / `${idx}-back` so a variant's front and back
  // uploads are tracked independently.
  const reportUploading = (key, isUploading) => {
    if (isUploading) uploadingIds.current.add(key); else uploadingIds.current.delete(key);
    onUploadingChange?.(uploadingIds.current.size > 0);
  };

  const update = (idx, patch) => {
    const next = value.map((v, i) => (i === idx ? { ...v, ...patch } : v));
    onChange(next);
  };

  const handleNameChange = (idx, name) => {
    const current = value[idx];
    // Freeze the id after first assignment so renaming a color never
    // orphans the variant id referenced by existing cart items / orders.
    if (current.id) {
      update(idx, { name });
      return;
    }
    const existingIds = new Set(value.map((v) => v.id).filter(Boolean));
    update(idx, { name, id: name.trim() ? slugForName(name, existingIds) : '' });
  };

  const addVariant = () => {
    onChange([...value, { id: '', name: '', name_en: '', hex: '#2ca9e1', image: '', imageBack: '', quantity: 0 }]);
  };

  const removeVariant = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <label style={labelStyle}>Variantes de color (opcional)</label>
      <p style={{ fontSize: '0.82rem', color: '#777', margin: '0 0 0.75rem' }}>
        Cada variante tiene su propio stock e imagen. Cuando hay al menos una,
        el cliente debe elegir un color antes de agregar el producto a la bolsa,
        y el stock/agotado del producto se calcula a partir de las variantes.
      </p>

      {value.map((variant, idx) => (
        <div
          key={idx}
          style={{
            border: '1px solid #e9ecef',
            borderRadius: '10px',
            padding: '1rem',
            marginBottom: '0.85rem',
            background: '#fafbfc',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0 1rem', alignItems: 'start' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={labelStyle}>Nombre del color</label>
              <input
                type="text"
                value={variant.name}
                onChange={(e) => handleNameChange(idx, e.target.value)}
                placeholder="Ej. Rojo"
                required
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={labelStyle}>Name (English) — opcional</label>
              <input
                type="text"
                value={variant.name_en || ''}
                onChange={(e) => update(idx, { name_en: e.target.value })}
                placeholder="e.g. Red"
                style={inputStyle}
              />
            </div>
            <button
              type="button"
              onClick={() => removeVariant(idx)}
              title="Eliminar variante"
              style={{
                marginTop: '1.6rem',
                background: 'none',
                border: 'none',
                color: '#dc3545',
                cursor: 'pointer',
                padding: '0.3rem',
              }}
            >
              <Trash2 size={18} />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={labelStyle}>Color (swatch)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="color"
                  value={variant.hex || '#2ca9e1'}
                  onChange={(e) => update(idx, { hex: e.target.value })}
                  style={{ width: 42, height: 38, padding: 0, border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer' }}
                />
                <input
                  type="text"
                  value={variant.hex || ''}
                  onChange={(e) => update(idx, { hex: e.target.value })}
                  placeholder="#E4405F"
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={labelStyle}>Cantidad en stock</label>
              <input
                type="number"
                value={variant.quantity ?? 0}
                onChange={(e) => update(idx, { quantity: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                min="0"
                step="1"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
            <ImageUploadField
              label="Imagen frontal de esta variante"
              value={variant.image || ''}
              onChange={(url) => update(idx, { image: url })}
              bucket={BUCKET}
              onUploadingChange={(isUploading) => reportUploading(`${idx}-front`, isUploading)}
            />
            <ImageUploadField
              label="Imagen trasera de esta variante (opcional)"
              value={variant.imageBack || ''}
              onChange={(url) => update(idx, { imageBack: url })}
              bucket={BUCKET}
              onUploadingChange={(isUploading) => reportUploading(`${idx}-back`, isUploading)}
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addVariant}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.4rem',
          padding: '0.5rem 1rem',
          background: 'white',
          border: '1px dashed #2ca9e1',
          borderRadius: '8px',
          color: '#2ca9e1',
          fontSize: '0.88rem',
          fontWeight: '600',
          cursor: 'pointer',
        }}
      >
        <Plus size={16} /> Agregar variante de color
      </button>
    </div>
  );
};

export default ColorVariantsField;
